import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    IconButton,
    FormLabel,
    Typography,
    Card,
    Tooltip,
} from '@mui/material';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { StrictModeDroppable } from '../StrictModeDroppable';
import { getAssets } from '../../actions/mediaAction';
import ExistingMediaUploadPopup from './ExistingMediaUploadPopup';
import DeleteIcon from '../../assets/images/icons/delete_icon.svg';
import AddMedia from '../../pages/media/AddMedia';

const MediaUpload = ({ workspaceId, name, onChange, value = [] }) => {
    const [files, setFiles] = useState(Array.isArray(value) ? value : []);
    const [existingMediaPopup, setExistingMediaPopup] = useState(false);
    const [newMediaPopup, setNewMediaPopup] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (value) {
            const formattedFiles = Array.isArray(value) ? value : [value];
            const validFiles = formattedFiles.filter(file => file && file._id && file.url);
            setFiles(validFiles);
        }
    }, [value]);


    const handleAddExistingMedia = (selectedAssets) => {
        const formattedFiles = selectedAssets.map(asset => ({
            _id: asset._id,
            title: asset.title,
            description: asset.description,
            url: asset.url,

        }))

        // Skip assets already attached — draggable ids must be unique.
        const existingIds = new Set(files.map(file => file._id));
        const updatedFiles = [...files, ...formattedFiles.filter(file => !existingIds.has(file._id))];
        setFiles(updatedFiles);
        onChange(updatedFiles);
    };

    const handleAddNewMedia = (newAddedAsset) => {
        const newAsset = newAddedAsset?.asset;
        
        if (!newAsset) return;

        const formattedFile = {
            _id: newAsset._id,
            title: newAsset.title || 'Untitled',
            description: newAsset.description || '',
            url: newAsset.url,
            fileName: newAsset.fileName
        };

        const updatedFiles = files.some(file => file._id === formattedFile._id)
            ? files
            : [...files, formattedFile];
        setFiles(updatedFiles);
        onChange(updatedFiles);

        setNewMediaPopup(false);
    };


    const handleRemoveFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);

        // If no files are left, pass an empty string to onChange
        if (newFiles.length === 0) {
            onChange("");
        } else {
            onChange(newFiles);
        }
    };

    const moveFile = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= files.length || fromIndex === toIndex) return;
        const newFiles = Array.from(files);
        const [moved] = newFiles.splice(fromIndex, 1);
        newFiles.splice(toIndex, 0, moved);
        setFiles(newFiles);
        onChange(newFiles);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        moveFile(result.source.index, result.destination.index);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <FormLabel component="legend" sx={{ color: 'black' }}>{name}</FormLabel>
            <Box
                sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    mb: 2,
                    mt: 1,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => setExistingMediaPopup(true)}
                    sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.light',
                            color: 'white'
                        }
                    }}
                >
                    Add Existing Media
                </Button>

                <Button
                    variant="outlined"
                    onClick={() => setNewMediaPopup(true)}
                    sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.light',
                            color: 'white'
                        }
                    }}
                >
                    Add New Media
                </Button>
            </Box>

            {files.length > 0 && (
                <>
                    {files.length > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Drag images or use the arrows to reorder.
                        </Typography>
                    )}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <StrictModeDroppable droppableId={`media-${name}`} direction="horizontal">
                            {(provided) => (
                                <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{ display: 'flex', overflowX: 'auto', pb: 1 }}
                                >
                                    {files.map((file, index) => (
                                        <Draggable key={file._id} draggableId={String(file._id)} index={index}>
                                            {(dragProvided, snapshot) => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    sx={{ mr: 2, flexShrink: 0 }}
                                                >
                                                    <Card
                                                        sx={{
                                                            position: 'relative',
                                                            width: '200px',
                                                            boxShadow: snapshot.isDragging ? 6 : undefined,
                                                        }}
                                                    >
                                                        <Box
                                                            {...dragProvided.dragHandleProps}
                                                            sx={{ cursor: 'grab' }}
                                                        >
                                                            <img
                                                                src={file.url}
                                                                alt={file.title}
                                                                style={{
                                                                    width: '200px',
                                                                    height: '100px',
                                                                    objectFit: 'cover',
                                                                    display: 'block'
                                                                }}
                                                            />
                                                            <DragIndicatorIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    left: 8,
                                                                    color: 'white',
                                                                    bgcolor: 'rgba(0,0,0,0.4)',
                                                                    borderRadius: 1
                                                                }}
                                                            />
                                                        </Box>
                                                        <IconButton
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                color: '#960101',
                                                                '&:hover': { bgcolor: 'grey.100' }
                                                            }}
                                                            onClick={() => handleRemoveFile(index)}
                                                        >
                                                            <img
                                                                src={DeleteIcon}
                                                                alt="delete icon"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        </IconButton>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
                                                            <Tooltip title="Move left">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={index === 0}
                                                                        onClick={() => moveFile(index, index - 1)}
                                                                    >
                                                                        <ChevronLeftIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Typography variant='subtitle1' noWrap sx={{ flex: 1, textAlign: 'center' }}>
                                                                {file.title || file.fileName}
                                                            </Typography>
                                                            <Tooltip title="Move right">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={index === files.length - 1}
                                                                        onClick={() => moveFile(index, index + 1)}
                                                                    >
                                                                        <ChevronRightIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    </Card>
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                </>
            )}

            <AddMedia
                isOpen={newMediaPopup}
                onClose={() => setNewMediaPopup(false)}
                onSubmitSuccess={handleAddNewMedia}
                editMode={false}
            />

            <ExistingMediaUploadPopup
                open={existingMediaPopup}
                onClose={() => setExistingMediaPopup(false)}
                onConfirm={handleAddExistingMedia}
                workspaceId={workspaceId}
            />
        </Box>
    );
};

export default MediaUpload;